import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import ReactDOM from 'react-dom';

const languages = [
  { code: 'fr', name: 'Français', flag: 'FR' },
  { code: 'en', name: 'English', flag: 'EN' },
  { code: 'es', name: 'Español', flag: 'ES' },
  { code: 'ar', name: 'العربية', flag: 'AR' }
];

// Inline SVG flags for reliable rendering
const FlagSVG = {
  fr: (
    <svg viewBox="0 0 900 600" className="w-full h-full">
      <rect width="300" height="600" fill="#002654"/>
      <rect x="300" width="300" height="600" fill="#FFFFFF"/>
      <rect x="600" width="300" height="600" fill="#CE1126"/>
    </svg>
  ),
  en: (
    <svg viewBox="0 0 60 30" className="w-full h-full">
      <clipPath id="s"><path d="M0,0 v30 h60 v-30 z"/></clipPath>
      <clipPath id="t"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z"/></clipPath>
      <g clipPath="url(#s)">
        <path d="M0,0 v30 h60 v-30 z" fill="#012169"/>
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6"/>
        <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t)" stroke="#C8102E" strokeWidth="4"/>
        <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10"/>
        <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6"/>
      </g>
    </svg>
  ),
  es: (
    <svg viewBox="0 0 750 500" className="w-full h-full">
      <rect width="750" height="500" fill="#c60b1e"/>
      <rect width="750" height="250" y="125" fill="#ffc400"/>
    </svg>
  ),
  ar: (
    <svg viewBox="0 0 900 600" className="w-full h-full">
      <rect fill="#006C35" width="900" height="600"/>
      <g fill="#fff">
        <path d="M450,170c-45,0-90,30-90,85c0,45,25,75,60,90v20h-35v25h115v-25h-35v-20c35-15,60-45,60-90c0-55-45-85-90-85h15z"/>
      </g>
    </svg>
  )
};

// Flag component using inline SVGs
const Flag = ({ code, className = "w-5 h-4" }) => {
  return (
    <span className={`${className} inline-block overflow-hidden rounded-sm border border-gray-200/50`}>
      {FlagSVG[code]}
    </span>
  );
};

const LanguageSwitcher = ({ variant = 'default' }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);
  const menuRef = React.useRef(null);

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside (but not on the menu itself)
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideButton = dropdownRef.current && !dropdownRef.current.contains(event.target);
      const isOutsideMenu = menuRef.current && !menuRef.current.contains(event.target);
      
      if (isOutsideButton && isOutsideMenu) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get button position for portal dropdown
  const getDropdownPosition = () => {
    if (!dropdownRef.current) return { top: 0, right: 0 };
    const rect = dropdownRef.current.getBoundingClientRect();
    const menuWidth = 150; // min-w-[150px]
    
    // On mobile, align to right edge of button to prevent cutoff
    const rightEdge = window.innerWidth - rect.right;
    
    return {
      top: rect.bottom + 8,
      right: Math.max(8, rightEdge) // At least 8px from right edge
    };
  };

  // Dropdown menu component (rendered via portal)
  const DropdownMenu = ({ position }) => {
    return ReactDOM.createPortal(
      <div 
        ref={menuRef}
        className="fixed bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[150px]"
        style={{ 
          top: position.top, 
          right: position.right,
          zIndex: 99999
        }}
      >
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            className={`w-full px-4 py-2.5 text-left flex items-center gap-3 hover:bg-gray-50 transition-colors ${
              lang.code === i18n.language ? 'bg-blue-50 text-[#3b82f6]' : 'text-gray-700'
            }`}
            data-testid={`lang-${lang.code}`}
          >
            <Flag code={lang.code} className="w-6 h-4" />
            <span className="text-sm font-medium">{lang.name}</span>
          </button>
        ))}
      </div>,
      document.body
    );
  };

  // Top bar variant (white text on blue background)
  if (variant === 'topbar') {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1.5 text-white/90 hover:text-white transition-colors text-sm px-2 py-1 rounded hover:bg-white/10"
          data-testid="language-switcher"
        >
          <Flag code={currentLang.code} className="w-5 h-3.5" />
          <span className="font-medium">{currentLang.code.toUpperCase()}</span>
          <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {isOpen && <DropdownMenu position={getDropdownPosition()} />}
      </div>
    );
  }

  if (variant === 'minimal') {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1.5 text-gray-600 hover:text-[#3b82f6] transition-colors text-sm"
          data-testid="language-switcher"
        >
          <Flag code={currentLang.code} className="w-5 h-3.5" />
          <span className="uppercase font-medium">{currentLang.code}</span>
          <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {isOpen && <DropdownMenu position={getDropdownPosition()} />}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 bg-white/90 hover:bg-white px-3 py-2 rounded-full border border-gray-200 shadow-sm transition-colors"
        data-testid="language-switcher"
      >
        <Flag code={currentLang.code} className="w-6 h-4" />
        <span className="text-sm font-medium text-gray-700">{currentLang.name}</span>
        <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && <DropdownMenu position={getDropdownPosition()} />}
    </div>
  );
};

export default LanguageSwitcher;
